import type { MarketEvent } from '../../api/client'
import { BelegeListe, alsBelege } from '../common/Belege'
import { Refs } from '../common/Refs'
import { Verknuepfungen } from '../common/Verknuepfungen'
import type { Verknuepfung } from '../../hooks/useVerknuepft'
import { STATUS_TEXTE, artText, statusTon } from '../../pages/eventVocabulary'
import { datumVoll, tagKurz, zeitHinweis, zeitraum } from './ereignisZeit'
import s from './EreignisDetail.module.scss'

const datum = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' })

/** Eine Zahl mit ihrer Einheit, oder ein Strich. */
function messwert(wert: string | null, einheit: string | null): string {
  if (!wert) return '—'
  return einheit ? `${wert} ${einheit}` : wert
}

/**
 * Das Ereignisdetail — der rechte Teil der Master-Detail-Ansicht.
 *
 * Oben das Blättern, darunter das Ereignis: Art, Status, Termin, Titel in der
 * Serife, dann Termin mit Hinweis, Serie, Erfassung und Herkunft. Der farbige
 * Streifen links trägt den Status. Darunter vier Felder: Betrifft, Erwartung
 * (Vorher, Prognose, Ist), Belege, Verknüpft.
 *
 * KEIN ZWEITER AUFRUF: Alles steht schon in der Zeile — bis auf die
 * Verknüpfungen, die die Seite nachschlägt.
 */
export function EreignisDetail({ item, position, onVorherige, onNaechste,
                                 verknuepft, verknuepftLaedt }: {
  item: MarketEvent
  /** „06.09.2026 · 2 von 3". */
  position: string
  onVorherige: () => void
  onNaechste: () => void
  verknuepft: Verknuepfung[]
  verknuepftLaedt: boolean
}) {
  const ton = statusTon(item.status)
  const hinweis = zeitHinweis(item)
  const belege = alsBelege(item.sources)

  return (
    <div className={s.detail}>
      <div className={s.blaettern}>
        <span>{position}</span>
        <span className={s.knoepfe}>
          <button type="button" onClick={onVorherige}>‹ vorheriges</button>
          <button type="button" onClick={onNaechste}>nächstes ›</button>
        </span>
      </div>

      <article className={s.artikel}>
        <div className={s.kopf}>
          <div className={s.streifen} data-ton={ton} />
          <div className={s.inhalt}>
            <div className={s.zeile}>
              <span className={s.art}>{artText(item)}</span>
              <span className={s.status} data-ton={ton}>
                {STATUS_TEXTE[item.status] ?? item.status}
              </span>
              <span className={s.datum}>{datumVoll(item)}</span>
            </div>
            <h2 className={s.titel}>{item.title}</h2>
            <div className={s.meta}>
              <span>
                Termin <b>{zeitraum(item)}</b>
                {hinweis && <> · {hinweis}</>}
              </span>
              {item.rescheduled_from && (
                <span>verschoben von <b>{tagKurz(item.rescheduled_from)}</b></span>
              )}
              {item.series && <span>Serie <b className={s.mono}>{item.series}</b></span>}
              <span>erfasst <b>{datum.format(new Date(item.created))}</b></span>
              <span>Herkunft <b>{item.source}</b></span>
            </div>
          </div>
        </div>

        <div className={s.felder}>
          <div className={s.feld}>
            <div className={s.feldTitel}>Betrifft</div>
            <Refs refs={item.targets} leer="Markt — kein einzelnes Ziel" />
          </div>
          <div className={s.feld}>
            <div className={s.feldTitel}>Erwartung</div>
            {/*
              Das Ergebnis steht fett, aber NICHT in Gruen oder Rot: Ob ein
              Wert ueber der Prognose gut ist, haengt an der Groesse — mehr
              Inflation ist keine gute Ueberraschung.
            */}
            <dl className={s.zahlen}>
              <div><dt>Vorher</dt><dd>{messwert(item.previous, item.unit)}</dd></div>
              <div><dt>Prognose</dt><dd>{messwert(item.forecast, item.unit)}</dd></div>
              <div><dt>Ist</dt><dd data-ist={item.actual ? 'true' : undefined}>
                {messwert(item.actual, item.unit)}
              </dd></div>
            </dl>
          </div>
          <div className={s.feld}>
            <div className={s.feldKopf}>
              <div className={s.feldTitel}>Belege</div>
              <span className={s.anzahl}>{belege.length}</span>
            </div>
            {belege.length === 0
              ? <span className={s.keine}>keine Belege</span>
              : <BelegeListe belege={belege} />}
          </div>
          <div className={s.feld}>
            <div className={s.feldTitel}>Verknüpft</div>
            <Verknuepfungen eintraege={verknuepft} laedt={verknuepftLaedt} />
          </div>
        </div>
      </article>
    </div>
  )
}
